import {
  DefaultError,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  AttachSaleToDeliveryReq,
  AttachSaleToDeliveryRes,
  CreateDeliveryReq,
  CreateDeliveryRes,
  EmailDeliveryPDFReq,
  EmailDeliveryPDFRes,
  GetDeliveryPDFReq,
  GetDeliveryPDFRes,
  GetScannedDeliveryReq,
  GetScannedDeliveryRes,
  RemoveSaleFromDeliveryReq,
  RemoveSaleFromDeliveryRes,
  StageSaleReq,
  StageSaleRes,
  UpdateDeliveryReq,
  UpdateDeliveryRes,
} from "gen/api/delivery/service_pb"
import { DeliveryServiceClient } from "gen/api/delivery/service_pb.client"
import { UUID } from "shared-ts/classes/uuid"
import { adaptDeliveryStatus, BaseDelivery } from "shared-ts/deliveries"

import { transport } from "./apiSlice"

const client = new DeliveryServiceClient(transport)

export function useCreateDeliveryMutation() {
  const queryClient = useQueryClient()
  return useMutation<CreateDeliveryRes, DefaultError, CreateDeliveryReq>({
    mutationFn: async (req) => {
      const { response } = await client.createDelivery(req)
      return response
    },
    onSuccess: () =>
      // Keep the mutation pending until affected queries refresh.
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["search", "deliveries"] }),
        queryClient.invalidateQueries({
          queryKey: ["search", "delivery-stops"],
        }),
        queryClient.invalidateQueries({ queryKey: ["search", "sales"] }),
      ]),
  })
}

const deliveryQuery = (id?: UUID) => ({
  queryKey: ["delivery", id?.toString(), id] as const,
  queryFn: async () => {
    const { response } = await client.getDelivery({ id })
    return response
  },
})

export function useGetDeliveryQuery(id?: UUID) {
  return useQuery({ ...deliveryQuery(id), enabled: !!id })
}

export function useGetLinkedDeliveriesQuery(
  ids: UUID[]
): Pick<BaseDelivery, "id" | "identifier" | "status">[] {
  return useQueries({
    queries: ids.map((id) => deliveryQuery(id)),
    combine: (results) =>
      results.flatMap(({ data }) =>
        data?.id
          ? [
              {
                id: UUID.fromPBOrThrow(data.id, "Missing delivery.id"),
                identifier: data.identifier,
                status: adaptDeliveryStatus(data.status),
              },
            ]
          : []
      ),
  })
}

export function useGetScannedDeliveryMutation() {
  return useMutation<
    GetScannedDeliveryRes,
    DefaultError,
    GetScannedDeliveryReq
  >({
    mutationFn: async (req) => {
      const { response } = await client.getScannedDelivery(req)
      return response
    },
  })
}

export function useGetDeliveryPDFMutation() {
  return useMutation<GetDeliveryPDFRes, DefaultError, GetDeliveryPDFReq>({
    mutationFn: async (req) => {
      const { response } = await client.getDeliveryPDF(req)
      return response
    },
  })
}

export function useUpdateDeliveryMutation() {
  const queryClient = useQueryClient()
  return useMutation<UpdateDeliveryRes, DefaultError, UpdateDeliveryReq>({
    scope: { id: "delivery" },
    mutationFn: async (req) => {
      const { response } = await client.updateDelivery(req)
      return response
    },
    onSuccess: () =>
      // Keep the mutation pending until affected queries refresh.
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["delivery"] }),
        queryClient.invalidateQueries({ queryKey: ["search", "deliveries"] }),
        queryClient.invalidateQueries({
          queryKey: ["search", "delivery-stops"],
        }),
        queryClient.invalidateQueries({ queryKey: ["search", "sales"] }),
      ]),
  })
}

export function useStageSaleMutation() {
  return useMutation<StageSaleRes, DefaultError, StageSaleReq>({
    mutationFn: async (req) => {
      const { response } = await client.stageSale(req)
      return response
    },
  })
}

export function useRemoveSaleFromDeliveryMutation() {
  return useMutation<
    RemoveSaleFromDeliveryRes,
    DefaultError,
    RemoveSaleFromDeliveryReq
  >({
    mutationFn: async (req) => {
      const { response } = await client.removeSaleFromDelivery(req)
      return response
    },
  })
}

export function useAttachSaleToDeliveryMutation() {
  return useMutation<
    AttachSaleToDeliveryRes,
    DefaultError,
    AttachSaleToDeliveryReq
  >({
    mutationFn: async (req) => {
      const { response } = await client.attachSaleToDelivery(req)
      return response
    },
  })
}

export function useEmailDeliveryPDFMutation() {
  return useMutation<EmailDeliveryPDFRes, DefaultError, EmailDeliveryPDFReq>({
    mutationFn: async (req) => {
      const { response } = await client.emailDeliveryPDF(req)
      return response
    },
  })
}
